/*
 * Home page: hero slideshow + "Shop the Edit" tabs. Vanilla, no library.
 * The hero is a scroll-snap track (swipeable without JS); this script adds
 * the counter, the slide bars and optional auto-rotate.
 */

if (!customElements.get('hp-hero')) {
  class HpHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hp-hero__track');
      this.slides = Array.from(this.querySelectorAll('.hp-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.hp-hero__dot'));
      this.count = this.querySelector('[data-hp-current]');

      const foot = this.querySelector('.hp-hero__foot');
      if (foot) {
        const setFoot = () => this.style.setProperty('--hp-foot-h', `${foot.offsetHeight}px`);
        setFoot();
        if (window.ResizeObserver) new ResizeObserver(setFoot).observe(foot);
      }

      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => {
        this.stop();
        this.go(i);
      }));

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const seconds = parseInt(this.dataset.autoplay, 10);
      if (seconds > 0 && !reduced && !window.Shopify?.designMode) {
        this.timer = setInterval(() => this.go((this.index + 1) % this.slides.length), seconds * 1000);
        ['pointerdown', 'focusin', 'wheel', 'touchstart'].forEach((type) =>
          this.addEventListener(type, () => this.stop(), { once: true, passive: true })
        );
      }

      document.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.findIndex((slide) => slide.contains(event.target));
        if (i > -1) this.go(i, false);
      });
    }

    disconnectedCallback() {
      this.stop();
    }

    stop() {
      clearInterval(this.timer);
    }

    go(i, smooth = true) {
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    }

    onScroll() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i === this.index || !this.slides[i]) return;
      this.index = i;
      this.slides.forEach((slide, n) => slide.setAttribute('aria-hidden', n === i ? 'false' : 'true'));
      this.dots.forEach((dot, n) => dot.setAttribute('aria-current', n === i ? 'true' : 'false'));
      if (this.count) this.count.textContent = String(i + 1).padStart(2, '0');
    }
  }

  customElements.define('hp-hero', HpHero);
}

if (!customElements.get('hp-tabs')) {
  class HpTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!step) return;
          event.preventDefault();
          const next = (i + step + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });

      document.addEventListener('shopify:block:select', (event) => {
        const i = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
        const p = this.panels().findIndex((panel) => panel === event.target);
        if (i > -1 || p > -1) this.select(i > -1 ? i : p);
      });
    }

    panels() {
      return this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    }

    select(i) {
      const panels = this.panels();
      this.tabs.forEach((tab, n) => {
        const on = n === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (panels[n]) panels[n].hidden = !on;
      });
    }
  }

  customElements.define('hp-tabs', HpTabs);
}
